// src/components/common/Sidebar.js
import React, { useState, useEffect } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { collection, query, where, onSnapshot, orderBy } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { useAuthUser } from '../../hooks/useAuthUser';
import { FiHome, FiGrid } from 'react-icons/fi';
import { getPlatformDisplay } from '../shared/utils/platformHelpers';

function Sidebar() {
    const [projects, setProjects] = useState([]);
    const [isHovered, setIsHovered] = useState(false);
    const location = useLocation();
    const { projectId } = useParams();
    const { userId } = useAuthUser();

    // Fetch projects
    useEffect(() => {
        if (!userId) return;

        const q = query(
            collection(db, 'projects'),
            where('userId', '==', userId),
            orderBy('updatedAt', 'desc')
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const projectList = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setProjects(projectList);
        }, (err) => {
        });

        return () => unsubscribe();
    }, [userId]);

    return (
        <aside 
            className={`fixed left-0 top-0 bottom-0 z-50 flex flex-col items-center py-8 transition-all duration-300 ease-in-out ${
                isHovered ? 'w-24 bg-warm-dark/95 backdrop-blur-xl border-r border-white/5 shadow-2xl' : 'w-2 bg-transparent'
            }`}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            {/* Trigger Zone / Ghost Tag */}
            {!isHovered && (
                <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1.5 h-16 bg-gradient-to-b from-slate-blue/50 to-rosy-taupe/50 rounded-r-full shadow-[0_0_15px_rgba(110,231,183,0.3)] animate-pulse"></div>
            )}

            {/* Content Container (Only visible when hovered) */}
            <div className={`flex flex-col gap-4 w-full px-4 transition-opacity duration-200 delay-100 ${isHovered ? 'opacity-100 visible' : 'opacity-0 invisible'}`}>
                
                {/* Logo / Home */}
                <Link
                    to="/dashboard"
                    className={`group relative w-14 h-14 rounded-2xl flex items-center justify-center transition-all duration-300 ${
                        location.pathname === '/dashboard'
                            ? 'bg-slate-blue/20 text-slate-blue shadow-glow ring-1 ring-slate-blue/50' 
                            : 'bg-white/5 text-cream/50 hover:bg-white/10 hover:text-cream'
                    }`}
                    title="Dashboard"
                >
                    <FiHome size={22} />
                    {location.pathname === '/dashboard' && (
                        <div className="absolute inset-0 rounded-2xl bg-slate-blue/10 animate-pulse"></div>
                    )}
                </Link>

                <div className="w-10 h-[1px] bg-white/5 mx-auto my-2"></div>

                {/* Project List */}
                <div className="flex flex-col gap-3 overflow-y-auto max-h-[calc(100vh-250px)] no-scrollbar py-2">
                    {projects.map(project => {
                        const platformDisplay = getPlatformDisplay(project);
                        const PlatformIcon = platformDisplay.Icon;
                        const isActive = projectId === project.id;

                        return (
                            <Link
                                key={project.id}
                                to={`/project/${project.id}`}
                                className={`group relative w-14 h-14 flex-shrink-0 rounded-2xl overflow-hidden transition-all duration-300 ${
                                    isActive 
                                        ? 'ring-2 ring-slate-blue shadow-glow' 
                                        : 'hover:scale-105 hover:ring-1 hover:ring-white/20'
                                }`}
                            >
                                {project.coverImageUrl ? (
                                    <img 
                                        src={project.coverImageUrl} 
                                        alt={project.projectName}
                                        className={`w-full h-full object-cover transition-opacity ${isActive ? 'opacity-100' : 'opacity-70 group-hover:opacity-100'}`}
                                    />
                                ) : (
                                    <div className={`w-full h-full flex items-center justify-center bg-gradient-to-br ${isActive ? 'from-slate-blue/20 to-rosy-taupe/20' : 'from-white/5 to-white/10'}`}>
                                        <PlatformIcon size={20} className={isActive ? 'text-slate-blue' : platformDisplay.color} />
                                    </div>
                                )}
                                
                                {/* Tooltip */}
                                <div className="absolute left-full ml-4 px-3 py-2 bg-warm-dark/95 backdrop-blur-xl border border-white/10 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 pointer-events-none shadow-xl">
                                    <span className="text-xs font-bold text-cream block">{project.projectName}</span>
                                    <span className={`text-[10px] uppercase tracking-wider ${platformDisplay.color}`}>{platformDisplay.name}</span>
                                </div>
                            </Link>
                        );
                    })}
                </div>

                <div className="w-10 h-[1px] bg-white/5 mx-auto my-2"></div>

                {/* New Project */}
                <Link
                    to="/create-project"
                    className="group relative w-14 h-14 rounded-2xl border border-dashed border-white/20 flex items-center justify-center hover:border-slate-blue/50 hover:bg-slate-blue/5 transition-all text-white/40 hover:text-slate-blue"
                    title="New Project"
                >
                    <FiGrid size={20} />
                    <div className="absolute left-full ml-4 px-3 py-2 bg-warm-dark/95 backdrop-blur-xl border border-white/10 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 pointer-events-none shadow-xl">
                        <span className="text-xs font-bold text-cream">Create New Project</span>
                    </div>
                </Link>
            </div>
        </aside>
    );
}

export default Sidebar;
